import { useState, type ReactNode } from "react";
import { Lock, Crown, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useOrgQuota } from "@/hooks/useOrgQuota";

interface PlanGateProps {
  children: ReactNode;
  allowedPlans: string[];
  featureName: string;
  /** Optional description for the blocked state */
  description?: string;
}

const plans = [
  {
    name: "Starter",
    key: "BASIC",
    price: "$49",
    period: "/mes",
    credits: "100 documentos",
    features: ["Procesamiento PDF", "1 usuario", "Soporte email"],
    highlighted: false,
  },
  {
    name: "Pro",
    key: "PRO",
    price: "$149",
    period: "/mes",
    credits: "500 documentos",
    features: ["Todo en Starter", "5 usuarios", "Mensajería y colaboración", "Soporte prioritario"],
    highlighted: true,
  },
  {
    name: "Enterprise",
    key: "ENTERPRISE",
    price: "Custom",
    period: "",
    credits: "1,000+ documentos",
    features: ["Todo en Pro", "Usuarios ilimitados", "Tracking en tiempo real", "API & ERP", "Soporte dedicado"],
    highlighted: false,
  },
];

export function PlanGate({ children, allowedPlans, featureName, description }: PlanGateProps) {
  const quota = useOrgQuota();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // While loading, render nothing to avoid flash
  if (quota.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-12">
        <p className="text-muted-foreground text-sm">Cargando...</p>
      </div>
    );
  }

  // Access granted
  if (allowedPlans.includes(quota.planType)) {
    return <>{children}</>;
  }

  // Determine minimum required plan for the CTA
  const minPlan = allowedPlans.includes("PRO") ? "Pro" : "Enterprise";

  return (
    <>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full text-center space-y-6">
          {/* Icon */}
          <div className="mx-auto w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Crown className="h-10 w-10 text-primary" />
          </div>

          {/* Title */}
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-foreground">
              Desbloquea {featureName}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {description ||
                `Esta funcionalidad está disponible a partir del plan ${minPlan}. Actualiza tu plan para acceder a herramientas avanzadas que optimizan tu operación de comercio exterior.`}
            </p>
          </div>

          {/* Current plan badge */}
          <div className="flex items-center justify-center gap-2">
            <span className="text-xs text-muted-foreground">Tu plan actual:</span>
            <Badge variant="outline" className="text-xs">
              {quota.planLabel}
            </Badge>
          </div>

          {/* CTA */}
          <Button
            onClick={() => setUpgradeOpen(true)}
            className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 glow-cyan-strong"
            size="lg"
          >
            <Sparkles className="h-4 w-4" />
            Ver Planes
          </Button>
        </div>
      </div>

      {/* Pricing modal */}
      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="max-w-3xl glass border-border">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-foreground text-center">
              Escala tu operación con <span className="text-gradient-cyan">KargoNode</span>
            </DialogTitle>
            <p className="text-sm text-muted-foreground text-center">
              Elige el plan que mejor se adapte a tu volumen de comercio exterior
            </p>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {plans.map((plan) => {
              const isCurrent = plan.key === quota.planType;
              return (
                <div
                  key={plan.name}
                  className={cn(
                    "rounded-xl p-5 border transition-all relative",
                    plan.highlighted
                      ? "border-primary/50 bg-primary/5 glow-cyan"
                      : "border-border bg-muted/20 hover:border-border/80",
                    isCurrent && "ring-2 ring-primary/30"
                  )}
                >
                  {isCurrent && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider text-primary bg-background px-2 rounded-full border border-primary/30">
                      Actual
                    </span>
                  )}
                  {plan.highlighted && !isCurrent && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-primary mb-2 block">
                      Más Popular
                    </span>
                  )}
                  <h4 className="text-lg font-bold text-foreground">{plan.name}</h4>
                  <div className="mt-2 mb-4">
                    <span className="text-3xl font-bold text-foreground">{plan.price}</span>
                    <span className="text-sm text-muted-foreground">{plan.period}</span>
                  </div>
                  <p className="text-xs text-primary font-medium mb-4">{plan.credits}</p>
                  <ul className="space-y-2 mb-5">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    size="sm"
                    disabled={isCurrent}
                    className={cn(
                      "w-full text-xs",
                      plan.highlighted && !isCurrent
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "bg-muted text-foreground hover:bg-muted/80"
                    )}
                  >
                    {isCurrent ? "Plan Actual" : "Seleccionar"}
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
